import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Chip,
  IconButton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Build as BuildIcon,
  DirectionsCar as CarIcon,
  Download as DownloadIcon,
  DateRange as DateRangeIcon,
  Print as PrintIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

// Mock data interfaces
interface SalesMetric {
  period: string;
  revenue: number;
  units: number;
  averagePrice: number;
  topBrand: string;
}

interface ServiceMetric {
  period: string;
  revenue: number;
  jobs: number;
  averageValue: number;
  satisfaction: number;
}

interface InventoryMetric {
  brand: string;
  model: string;
  inStock: number;
  onOrder: number;
  averageAge: number;
  value: number;
}

interface CustomerMetric {
  type: string;
  count: number;
  revenue: number;
  satisfaction: number;
  retention: number;
}

export const ReportsAnalytics: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [timeRange, setTimeRange] = useState('month');

  // Mock data
  const salesMetrics: SalesMetric[] = [
    {
      period: 'March 2024',
      revenue: 45800000,
      units: 8,
      averagePrice: 5725000,
      topBrand: 'Ferrari',
    },
    {
      period: 'February 2024',
      revenue: 38500000,
      units: 6,
      averagePrice: 6416667,
      topBrand: 'McLaren',
    },
    {
      period: 'January 2024',
      revenue: 42300000,
      units: 7,
      averagePrice: 6042857,
      topBrand: 'Porsche',
    },
  ];

  const serviceMetrics: ServiceMetric[] = [
    {
      period: 'March 2024',
      revenue: 2850000,
      jobs: 45,
      averageValue: 63333,
      satisfaction: 98,
    },
    {
      period: 'February 2024',
      revenue: 2450000,
      jobs: 38,
      averageValue: 64474,
      satisfaction: 97,
    },
    {
      period: 'January 2024',
      revenue: 2650000,
      jobs: 42,
      averageValue: 63095,
      satisfaction: 96,
    },
  ];

  const inventoryMetrics: InventoryMetric[] = [
    {
      brand: 'Ferrari',
      model: 'SF90 Stradale',
      inStock: 2,
      onOrder: 1,
      averageAge: 45,
      value: 8500000,
    },
    {
      brand: 'McLaren',
      model: '765LT',
      inStock: 1,
      onOrder: 2,
      averageAge: 30,
      value: 7800000,
    },
    {
      brand: 'Porsche',
      model: '911 GT3 RS',
      inStock: 3,
      onOrder: 2,
      averageAge: 60,
      value: 4200000,
    },
    {
      brand: 'Lamborghini',
      model: 'Huracán STO',
      inStock: 2,
      onOrder: 1,
      averageAge: 15,
      value: 6500000,
    },
  ];

  const customerMetrics: CustomerMetric[] = [
    {
      type: 'VIP',
      count: 15,
      revenue: 28500000,
      satisfaction: 98,
      retention: 95,
    },
    {
      type: 'Premium',
      count: 25,
      revenue: 15200000,
      satisfaction: 92,
      retention: 88,
    },
    {
      type: 'Standard',
      count: 45,
      revenue: 8500000,
      satisfaction: 85,
      retention: 75,
    },
  ];

  const formatCurrency = (amount: number) => {
    return `R ${amount.toLocaleString()}`;
  };

  const getPercentageChange = (current: number, previous: number) => {
    const change = ((current - previous) / previous) * 100;
    return change.toFixed(1);
  };

  const handleExportClick = () => {
    let headers: string[] = [];
    let data: string[][] = [];

    // Prepare data based on current tab
    switch (currentTab) {
      case 0: // Sales Performance
        headers = ['Period', 'Revenue', 'Units Sold', 'Average Price', 'Top Brand'];
        data = salesMetrics.map(metric => [
          metric.period,
          metric.revenue.toString(),
          metric.units.toString(),
          metric.averagePrice.toString(),
          metric.topBrand
        ]);
        break;
      case 1: // Service Analytics
        headers = ['Period', 'Revenue', 'Jobs Completed', 'Average Value', 'Satisfaction'];
        data = serviceMetrics.map(metric => [
          metric.period,
          metric.revenue.toString(),
          metric.jobs.toString(),
          metric.averageValue.toString(),
          `${metric.satisfaction}%`
        ]);
        break;
      case 2: // Inventory Analysis
        headers = ['Brand', 'Model', 'In Stock', 'On Order', 'Average Age (Days)', 'Value'];
        data = inventoryMetrics.map(metric => [
          metric.brand,
          metric.model,
          metric.inStock.toString(),
          metric.onOrder.toString(),
          metric.averageAge.toString(),
          metric.value.toString()
        ]);
        break;
      case 3: // Customer Insights
        headers = ['Type', 'Count', 'Revenue', 'Satisfaction', 'Retention Rate'];
        data = customerMetrics.map(metric => [
          metric.type,
          metric.count.toString(),
          metric.revenue.toString(),
          `${metric.satisfaction}%`,
          `${metric.retention}%`
        ]);
        break;
    }

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...data.map(row => row.join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      const tabNames = ['sales', 'service', 'inventory', 'customer'];
      link.setAttribute('href', url);
      link.setAttribute('download', `${tabNames[currentTab]}_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrintClick = () => {
    window.print();
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            Reports & Analytics
          </Typography>
          <Stack direction="row" spacing={2}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="week">Last Week</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
                <MenuItem value="quarter">Last Quarter</MenuItem>
                <MenuItem value="year">Last Year</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportClick}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrintClick}
            >
              Print
            </Button>
          </Stack>
        </Box>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Total Revenue
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {formatCurrency(salesMetrics[0].revenue)}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TrendingUpIcon color="success" />
                  <Typography variant="body2" color="success.main">
                    +{getPercentageChange(salesMetrics[0].revenue, salesMetrics[1].revenue)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    vs last month
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Units Sold
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {salesMetrics[0].units}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TrendingUpIcon color="success" />
                  <Typography variant="body2" color="success.main">
                    +{getPercentageChange(salesMetrics[0].units, salesMetrics[1].units)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    vs last month
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Service Revenue
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {formatCurrency(serviceMetrics[0].revenue)}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TrendingUpIcon color="success" />
                  <Typography variant="body2" color="success.main">
                    +{getPercentageChange(serviceMetrics[0].revenue, serviceMetrics[1].revenue)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    vs last month
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Customer Satisfaction
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {serviceMetrics[0].satisfaction}%
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TrendingUpIcon color="success" />
                  <Typography variant="body2" color="success.main">
                    +{getPercentageChange(serviceMetrics[0].satisfaction, serviceMetrics[1].satisfaction)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    vs last month
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => setCurrentTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Sales Performance" />
            <Tab label="Service Analytics" />
            <Tab label="Inventory Analysis" />
            <Tab label="Customer Insights" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {currentTab === 0 && (
              <TableContainer>
                <Typography variant="h6" gutterBottom>
                  Sales Performance Trends
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Units Sold</TableCell>
                      <TableCell align="right">Average Price</TableCell>
                      <TableCell>Top Brand</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesMetrics.map((metric) => (
                      <TableRow key={metric.period}>
                        <TableCell>{metric.period}</TableCell>
                        <TableCell align="right">{formatCurrency(metric.revenue)}</TableCell>
                        <TableCell align="right">{metric.units}</TableCell>
                        <TableCell align="right">{formatCurrency(metric.averagePrice)}</TableCell>
                        <TableCell>{metric.topBrand}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {currentTab === 1 && (
              <TableContainer>
                <Typography variant="h6" gutterBottom>
                  Service Department Performance
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Jobs Completed</TableCell>
                      <TableCell align="right">Average Value</TableCell>
                      <TableCell align="right">Satisfaction</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {serviceMetrics.map((metric) => (
                      <TableRow key={metric.period}>
                        <TableCell>{metric.period}</TableCell>
                        <TableCell align="right">{formatCurrency(metric.revenue)}</TableCell>
                        <TableCell align="right">{metric.jobs}</TableCell>
                        <TableCell align="right">{formatCurrency(metric.averageValue)}</TableCell>
                        <TableCell align="right">{metric.satisfaction}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {currentTab === 2 && (
              <TableContainer>
                <Typography variant="h6" gutterBottom>
                  Current Inventory Status
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Brand & Model</TableCell>
                      <TableCell align="right">In Stock</TableCell>
                      <TableCell align="right">On Order</TableCell>
                      <TableCell align="right">Average Age (Days)</TableCell>
                      <TableCell align="right">Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventoryMetrics.map((metric) => (
                      <TableRow key={`${metric.brand}-${metric.model}`}>
                        <TableCell>
                          <Typography variant="body2">
                            {metric.brand} {metric.model}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{metric.inStock}</TableCell>
                        <TableCell align="right">{metric.onOrder}</TableCell>
                        <TableCell align="right">{metric.averageAge}</TableCell>
                        <TableCell align="right">{formatCurrency(metric.value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {currentTab === 3 && (
              <TableContainer>
                <Typography variant="h6" gutterBottom>
                  Customer Segment Analysis
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer Type</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Satisfaction</TableCell>
                      <TableCell align="right">Retention Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerMetrics.map((metric) => (
                      <TableRow key={metric.type}>
                        <TableCell>
                          <Chip
                            label={metric.type}
                            color={
                              metric.type === 'VIP' ? 'error' :
                              metric.type === 'Premium' ? 'warning' : 'info'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{metric.count}</TableCell>
                        <TableCell align="right">{formatCurrency(metric.revenue)}</TableCell>
                        <TableCell align="right">{metric.satisfaction}%</TableCell>
                        <TableCell align="right">{metric.retention}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>
      </Box>
    </DashboardLayout>
  );
}; 